import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Phone, RadioTower, Settings } from "lucide-react";

import { Button, StateDot, type StateDotStatus } from "~/components";
import { CallBanner } from "~/components/intake/CallBanner";
import { NewLeadDialog } from "~/components/intake/NewLeadDialog";
import { ThreadTab } from "~/components/intake/ThreadTab";
import { boardCasesQueryOptions, isDeadEnd } from "~/lib/cases";
import { threadsListQueryOptions, railThreads, useCallLanding, useOpenMatter } from "~/lib/threads";
import { queryClient } from "~/lib/queryClient";
import { VOICE_PROVIDER } from "~/lib/voice/callKey";
import { useVoiceDevice, VoiceDeviceContext, type VoicePhase } from "~/lib/voice/useVoiceDevice";

import * as styles from "./_app.alpha.css";

// Alpha layout route — the workspace frame from DESIGN.md §5. The thread
// rail is Alpha's permanent furniture: present on every Alpha screen, so a
// rep never loses sight of a live call because they opened a report.
//
// The rail is a RECENCY WORKING SET (TMF-339): needs-attention first, newest
// first, five slots. Which threads those are is lib/threads.railThreads'
// call — the route renders what it returns.
//
// FOCUS IS THE ROUTE. The focused thread is /alpha/threads/$threadId;
// nothing focused is /alpha (the board). That makes the no-interruption
// rule (§9) structural: data updates can repaint a rail slot's dot or
// badge — and can re-target a slot when a live call arrives — but only an
// explicit rep action causes navigation, so background threads are
// incapable of stealing focus. The actions are a click, Ctrl/Alt+digit, Esc,
// and answering or placing a call (when that lands: lib/threads.useCallLanding).
export const Route = createFileRoute("/_app/alpha")({
  loader: async () => {
    await Promise.all([
      queryClient.ensureQueryData(threadsListQueryOptions),
      queryClient.ensureQueryData(boardCasesQueryOptions),
    ]);
  },
  component: AlphaLayout,
});

const ICON_SIZE = 15;

/** What a slot says when the matter it is on is not on the board's page. */
const UNKNOWN_PERSON = "Unknown matter";

// What the phone indicator says, per phase. The dot's liveness vocabulary is
// the closed set the component owns; this is the mapping onto it, in one place
// so the rail and the banner can never disagree about what "ringing" looks
// like. `active` (the pulsing one) is reserved for a call that is actually
// happening — a phone merely waiting to be rung must not pulse at a rep all
// day.
const PHONE_INDICATOR: Record<VoicePhase, { status: StateDotStatus; label: string }> = {
  offline: { status: "idle", label: "Phone offline" },
  registering: { status: "scheduled", label: "Phone connecting" },
  ready: { status: "waiting", label: "Phone ready" },
  ringing: { status: "active", label: "Ringing" },
  "in-call": { status: "active", label: "On a call" },
  error: { status: "breach", label: "Phone unavailable" },
};

function AlphaLayout() {
  const { data: threadCol } = useSuspenseQuery(threadsListQueryOptions);
  // The matters the rail speaks about. A thread row carries none of them
  // (TMF-360): it carries the matter's id, and the collection beside it is the
  // board's own, already cached.
  const { data: caseCol } = useSuspenseQuery(boardCasesQueryOptions);
  // ONE device for the whole workspace: mounted here because the frame is what
  // outlives every screen a rep moves between, and a second device on the same
  // identity would have the vendor offering one call to two sockets.
  const phone = useVoiceDevice();
  const navigate = useNavigate();
  // threadId is present only when a workspace child route is focused.
  const params = useParams({ strict: false }) as { threadId?: string };
  const focusedId = params.threadId;

  const href = useRouterState({ select: (s) => s.location.href });
  const landOnThread = useCallback(
    (threadId: string) => void navigate({ to: "/alpha/threads/$threadId", params: { threadId } }),
    [navigate],
  );
  useCallLanding(VOICE_PROVIDER, phone.callSid, href, landOnThread);
  const openMatter = useOpenMatter();
  const [creating, setCreating] = useState(false);

  // The rail's five slots, most-wanted first. Slots RE-TARGET as threads
  // arrive and change state — a live call belongs at the top the moment it
  // lands, and a rail that held its old order would be showing the rep the
  // wrong five.
  const matters = caseCol._embedded ?? [];
  const settledCaseIds = useMemo(
    () => new Set(matters.filter((m) => isDeadEnd(m.stage)).map((m) => m.id)),
    [matters],
  );
  const rail = useMemo(
    () => railThreads(threadCol._embedded ?? [], settledCaseIds),
    [threadCol, settledCaseIds],
  );

  /** What a slot's conversation is called: the matter's own name. A matter the
   *  board's page does not hold, which is the Filevine-synced one with no
   *  funnel, has nothing to name here. */
  const nameByCaseId = useMemo(() => {
    const names = new Map<string, string>();
    for (const matter of matters) {
      if (matter.name !== "") names.set(matter.id, matter.name);
    }
    return names;
  }, [matters]);

  // Keyboard-first (§10): Ctrl/Alt+1..5 focuses a rail slot, Esc returns
  // to Tower. The digits address the slots AS LISTED — the rail's order is
  // the keyboard's map, so what the rep sees in slot 3 is what Ctrl+3 opens.
  // Alt is the reliable binding on Windows/Chrome (Ctrl+digit is a browser
  // tab shortcut there); both are bound so whichever the browser lets
  // through works.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      // Never steal a keystroke from a field the rep is typing in — Esc over
      // the note canvas used to navigate away mid-call.
      const target = e.target;
      if (
        target instanceof Element &&
        target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
      if (e.key === "Escape") {
        void navigate({ to: "/alpha" });
        return;
      }
      if ((e.ctrlKey || e.altKey) && !e.shiftKey && !e.metaKey) {
        const slot = Number(e.key);
        if (slot >= 1 && slot <= rail.length) {
          e.preventDefault();
          void navigate({
            to: "/alpha/threads/$threadId",
            params: { threadId: rail[slot - 1]!.id },
          });
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rail, navigate]);

  return (
    <div className={styles.root}>
      <aside className={styles.rail} aria-label="Alpha navigation and threads">
        <nav className={styles.railNav}>
          {/* On workspace screens no destination is active — focus belongs
              to a thread, so Tower only lights up at exactly /alpha. */}
          <Link
            to="/alpha"
            activeOptions={{ exact: true }}
            activeProps={{ className: styles.navLinkActive }}
            inactiveProps={{ className: styles.navLink }}
          >
            <RadioTower size={ICON_SIZE} />
            Tower
          </Link>
          <Link
            to="/alpha/settings"
            activeProps={{ className: styles.navLinkActive }}
            inactiveProps={{ className: styles.navLink }}
          >
            <Settings size={ICON_SIZE} />
            Settings
          </Link>
        </nav>
        <div className={styles.phone} title={phone.error ?? undefined}>
          <Phone size={ICON_SIZE} />
          <StateDot
            status={PHONE_INDICATOR[phone.phase].status}
            label={PHONE_INDICATOR[phone.phase].label}
          />
        </div>
        <div className={styles.railLabel}>Threads</div>
        <div className={styles.slots}>
          {rail.length === 0 ? (
            <div className={styles.railEmpty}>No open threads</div>
          ) : (
            rail.map((t, i) => (
              <ThreadTab
                key={t.id}
                thread={t}
                name={nameByCaseId.get(t.case_id) ?? UNKNOWN_PERSON}
                focused={t.id === focusedId}
                shortcut={i + 1}
              />
            ))
          )}
        </div>
        <div className={styles.railFoot}>
          <Button
            type="button"
            variant="primary"
            size="sm"
            className={styles.railAction}
            onClick={() => setCreating(true)}
          >
            Create new lead
          </Button>
        </div>
      </aside>
      {creating ? (
        <NewLeadDialog
          onCancel={() => setCreating(false)}
          onCreated={(matter) => {
            setCreating(false);
            openMatter.open(matter);
          }}
        />
      ) : null}
      <main className={styles.stage}>
        {/* Above the stage, on every Alpha screen: a call must be answerable
            wherever the rep happens to be standing. It renders nothing at all
            unless there is one. */}
        <CallBanner device={phone} />
        {/* The screens inside place calls on this same device (TMF-483). */}
        <VoiceDeviceContext.Provider value={phone}>
          <Outlet />
        </VoiceDeviceContext.Provider>
      </main>
    </div>
  );
}
